import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ShieldCheck, Star, MapPin } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { topGun } from '@/constants/topgun'

const trust = [
    { icon: Star, label: `${topGun.rating.toFixed(1)} across ${topGun.reviewCount} Google reviews` },
    { icon: ShieldCheck, label: `${topGun.guaranteeYears}-year guarantee, terms in your contract` },
    { icon: MapPin, label: "Serving homeowners across Kansas & Missouri" }
]

export const TopGunHero: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-ink text-cream'>
            <div className='absolute inset-0 -z-10 animate-door-reveal' style={{ animationDelay: 'var(--enter-delay, 0ms)' }}>
                <div className='absolute inset-0 animate-hero-zoom' style={{ animationDelay: 'var(--enter-delay, 0ms)' }}>
                    <Image
                        src='/images/topgun/hero.webp'
                        alt="A Top Gun technician checking a front porch slab after lifting it back into place"
                        fill
                        priority
                        sizes='100vw'
                        className='object-cover object-[60%_center]'
                    />
                </div>

                <div className='absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/15 lg:from-ink/85 lg:via-ink/40 lg:to-transparent' aria-hidden='true' />
                <div className='absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/50' aria-hidden='true' />
                <div className='bg-noise absolute inset-0 opacity-[0.06] mix-blend-overlay' aria-hidden='true' />
            </div>

            <Container className='flex flex-1 flex-col justify-between gap-14 pb-8 pt-40 md:pb-10 lg:pt-48'>
                <div className='max-w-3xl'>
                    <Enter delay={250}>
                        <Eyebrow tone='light'>Top Gun Concrete Lifting · Kansas & Missouri</Eyebrow>
                    </Enter>

                    <HeroTitle
                        text="We lift what sinks."
                        accent={['sinks.']}
                        accentClassName='text-ember-soft'
                        baseDelay={420}
                        className='mt-7 max-w-[13ch] text-display font-semibold text-cream'
                    />

                    <Enter delay={900} className='mt-8 max-w-lg text-lead text-pretty text-cream/75'>
                        Sunken driveways, sidewalks, patios and steps raised back into place. No tear-out, and most jobs are done the same day.
                    </Enter>

                    <Enter delay={1050} className='mt-10 flex flex-wrap gap-3'>
                        <ButtonLink href={topGun.estimatorHref} size='lg' target='_blank' rel='noopener noreferrer'>
                            Open the Easy Estimator
                        </ButtonLink>

                        <ButtonLink href={topGun.phoneHref} variant='glass' size='lg' icon='phone'>
                            Call {topGun.phone}
                        </ButtonLink>
                    </Enter>
                </div>

                <Enter delay={1200}>
                    <div className='grid overflow-hidden rounded-[28px] border border-white/12 bg-ink/35 backdrop-blur-xl md:grid-cols-[1fr_1fr_1fr_1.15fr]'>
                        {trust.map(item => (
                            <div key={item.label} className='flex items-center gap-3 border-b border-white/10 px-5 py-4 text-[14.5px] text-cream/85 md:border-b-0 md:border-r md:px-6 md:py-6'>
                                <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-white/[0.08]'>
                                    <item.icon className='size-4 text-ember-soft' aria-hidden='true' />
                                </span>
                                {item.label}
                            </div>
                        ))}

                        <div className='hidden items-center justify-between gap-4 bg-white/[0.06] px-6 py-6 md:flex'>
                            <div>
                                <p className='font-mono text-[10.5px] uppercase tracking-[0.2em] text-cream/60'>
                                    A familiar home
                                </p>

                                <p className='mt-2 font-heading text-[1.15rem] font-semibold tracking-[-0.02em]'>
                                    A firmer footing.
                                </p>
                            </div>
                        </div>
                    </div>
                </Enter>
            </Container>
        </section>
    )
}
